import { fetchFromRTM, subscribeToRTM } from './index';
import * as constants from './constants';
import { ACTIVITY } from 'services/ActivityService/elementTypes';
import './typedef';

class PublicationResolver {
  constructor(connection) {
    this.connection = connection;
  }

  _getConfigSettings = (settings, reviewProductId) => {
    const configSettings = {
      settings: {
        ...settings,
        productId: reviewProductId,
        originalProductId: settings.productId,
      },
    };

    return JSON.stringify(configSettings);
  }

  createPublication = async ({
    accountId,
    activityId,
    title,
    author,
    description,
    exportId,
    format,
    settings,
    reviewProductId,
    market,
  }) => {
    const message = {
      type: constants.RTM_PUBLICATION_CREATE_REQUEST,
      accountId,
      activityId,
      publicationTitle: title,
      author,
      description,
      version: format ? format : "default", // Note: Re-purposing. This is so BE can separate the same activityId on response to publicationItems
      config: settings ? this._getConfigSettings(settings, reviewProductId) : '{}',
      exportId,
      outputType: format,
      ...(market && { market: market }),
    };
    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_PUBLICATION_CREATE_REQUEST_OK,
      constants.RTM_PUBLICATION_CREATE_REQUEST_ERROR,
      null,
      true
    );
    return response;
  };

  deletePublication = async ({ publicationId, activityId, version }) => {
    const message = {
      type: constants.RTM_PUBLICATION_HISTORY_DELETE,
      publicationId,
      activityId,
      version: version ? version : "default", // Note: Re-purposing. This is so BE can separate the same activityId on response to publicationItems
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_PUBLICATION_HISTORY_DELETE_OK,
      constants.RTM_PUBLICATION_HISTORY_DELETE_ERROR,
      null,
      true
    );
    return response;
  };

  _ePubPublish = async ({ activityId, type = ACTIVITY, metadata, market }) => {
    const message = {
      type: constants.RTM_AUTHOR_ELEMENT_EXPORT_REQUEST,
      elementId: activityId,
      // assuming only activity type for now
      elementType: type,
      ...(market && { market: market }),
      metadata,
      exportType: constants.RTM_AUTHOR_ELEMENT_EXPORT_TYPE_ETEXT,
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_AUTHOR_ELEMENT_EXPORT_REQUEST_OK,
      constants.RTM_AUTHOR_ELEMENT_EXPORT_REQUEST_ERROR
    );
    return response;
  };

  _inDesignPublish = async ({ activityId, type = ACTIVITY, metadata, market }) => {
    const message = {
      type: constants.RTM_AUTHOR_ELEMENT_EXPORT_REQUEST,
      elementId: activityId,
      // assuming only activity type for now
      elementType: type,
      metadata: metadata,
      ...(market && { market: market }),
      exportType: constants.RTM_AUTHOR_ELEMENT_EXPORT_TYPE_INDESIGN,
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_AUTHOR_ELEMENT_EXPORT_REQUEST_OK,
      constants.RTM_AUTHOR_ELEMENT_EXPORT_REQUEST_ERROR
    );
    return response;
  };

  // eslint-disable-next-line no-unused-vars
  _channelPartnerPublish = async ({
    activityId,
    type = ACTIVITY,
    metadata,
    market,
  }) => {
    const message = {
      type: constants.RTM_AUTHOR_ELEMENT_EXPORT_REQUEST,
      elementId: activityId,
      // assuming only activity type for now
      elementType: type,
      metadata: metadata,
      ...(market && { market: market }),
      exportType: constants.RTM_AUTHOR_ELEMENT_EXPORT_TYPE_CHANNEL_PARTNER,
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_AUTHOR_ELEMENT_EXPORT_REQUEST_OK,
      constants.RTM_AUTHOR_ELEMENT_EXPORT_REQUEST_ERROR
    );
    return response;
  };

  _classOnDemandPublish = async ({
    activityId,
    productId,
    publicationId,
    ltiConsumerCredential,
    settings,
  }) => {
    const message = {
      type: constants.RTM_PUBLICATION_CLASS_ON_DEMAND_PUBLISH_REQUEST,
      activityId,
      productId,
      publicationId,
      ltiConsumerCredential,
      ...(settings && { settings: JSON.stringify(settings) }),
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_PUBLICATION_CLASS_ON_DEMAND_PUBLISH_REQUEST_OK,
      constants.RTM_PUBLICATION_CLASS_ON_DEMAND_PUBLISH_REQUEST_ERROR,
      "",
      true
    );
    return response;
  };

  _pearsonPlusPublish = async ({ activityId, productId, title, tocLevel, market }) => {
    const message = {
      type: constants.RTM_PUBLICATION_PEARSON_PLUS_PUBLISH_REQUEST,
      activityId,
      productId,
      ...(market && { market: market }),
      title,
      tocLevel,
    };
    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_PUBLICATION_PEARSON_PLUS_PUBLISH_REQUEST_OK,
      constants.RTM_PUBLICATION_PEARSON_PLUS_PUBLISH_REQUEST_ERROR,
      undefined,
      true
    );
    return response;
  };

  rePublishPearsonPlus = async ({ activityId, productId, bookId, cohortId, deploymentId, tocLevel, market }) => {
    const message = {
      type: constants.RTM_PUBLICATION_PEARSON_PLUS_REPUBLISH_REQUEST,
      activityId,
      productId,
      bookId,
      ...(market && { market: market }),
      cohortId,
      deploymentId,
      tocLevel,
    };
    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_PUBLICATION_PEARSON_PLUS_REPUBLISH_REQUEST_OK,
      constants.RTM_PUBLICATION_PEARSON_PLUS_REPUBLISH_REQUEST_ERROR,
      undefined,
      true
    );
    return response;
  };

  _pearsonPlusTestOnlyPublish = async ({ activityId, productId, title }) => {
    const message = {
      type: constants.RTM_PUBLICATION_PEARSON_PLUS_TEST_PUBLISH_REQUEST,
      activityId,
      productId,
      title,
    };
    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_PUBLICATION_PEARSON_PLUS_TEST_PUBLISH_REQUEST_OK,
      constants.RTM_PUBLICATION_PEARSON_PLUS_TEST_PUBLISH_REQUEST_ERROR
    );
    return response;
  };

  rePublishPearsonPlusTestOnly = async ({ activityId, productId, cohortId, deploymentId }) => {
    const message = {
      type: constants.RTM_PUBLICATION_PEARSON_PLUS_TEST_REPUBLISH_REQUEST,
      activityId,
      productId,
      cohortId,
      deploymentId,
    };
    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_PUBLICATION_PEARSON_PLUS_TEST_REPUBLISH_REQUEST_OK,
      constants.RTM_PUBLICATION_PEARSON_PLUS_TEST_REPUBLISH_REQUEST_ERROR
    );
    return response;
  };

  exportPearsonPlus = async (activityId, metadata, market, type = ACTIVITY) => {
    const message = {
      type: constants.RTM_AUTHOR_ELEMENT_EXPORT_REQUEST,
      elementId: activityId,
      metadata,
      elementType: type,
      ...(market && { market: market }),
      exportType: constants.RTM_AUTHOR_ELEMENT_EXPORT_TYPE_PPLUS,
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_AUTHOR_ELEMENT_EXPORT_REQUEST_OK,
      constants.RTM_AUTHOR_ELEMENT_EXPORT_REQUEST_ERROR
    );
    return response;
  };

  exportPearsonPlusTest = async (activityId, metadata, type = "ACTIVITY") => {
    const message = {
      type: constants.RTM_AUTHOR_ELEMENT_EXPORT_REQUEST,
      elementId: activityId,
      metadata,
      elementType: type,
      exportType: constants.RTM_AUTHOR_ELEMENT_EXPORT_TYPE_PPLUS_TEST,
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_AUTHOR_ELEMENT_EXPORT_REQUEST_OK,
      constants.RTM_AUTHOR_ELEMENT_EXPORT_REQUEST_ERROR
    );
    return response;
  };

  getOculusStatus = async (bookId) => {
    const message = {
      type: constants.RTM_PUBLICATION_OCULUS_STATUS,
      bookId,
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_PUBLICATION_OCULUS_STATUS_OK,
      constants.RTM_PUBLICATION_OCULUS_STATUS_ERROR
    );
    return response.oculusData;
  };

  /**
   */
  getCoursewarePublicationStatus = async ({ activityId, publicationOutputType }) => {
    const message = {
      type: constants.RTM_PUBLICATION_COURSEWARE_STATUS,
      activityId,
      publicationOutputType,
    };
    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_PUBLICATION_COURSEWARE_STATUS_OK,
      constants.RTM_PUBLICATION_COURSEWARE_STATUS_ERROR
    );
    return response;
  };

  /**
   */
  provisionExternalUser = async (userDetails) => {
    const message = {
      type: constants.RTM_PROVISION_EXTERNAL_USER,
      accountDetails: userDetails,
    };
    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_PROVISION_EXTERNAL_USER_OK,
      constants.RTM_PROVISION_EXTERNAL_USER_ERROR,
    );
    return response;
  };

  /**
 * Checks if a specific element has been published.
 */
  isElementPublished = async ({ activityId, elementId }) => {
    const message = {
      type: constants.RTM_IS_ELEMENT_PUBLISHED,
      activityId,
      elementId,
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_IS_ELEMENT_PUBLISHED_OK,
      constants.RTM_IS_ELEMENT_PUBLISHED_ERROR
    );

    return response.isPublished;
  };


  publish = async ({ publication, format }) => {
    const response = await {
      EPUB_ETEXT: this._ePubPublish,
      EPUB_CHANNEL_PARTNER: this._channelPartnerPublish,
      IN_DESIGN: this._inDesignPublish,
      BRONTE_CLASSES_ON_DEMAND: this._classOnDemandPublish,
      BRONTE_PEARSON_PLUS: this._pearsonPlusPublish,
      BRONTE_PEARSON_PLUS_TEST_ONLY: this._pearsonPlusTestOnlyPublish,
    }[format](publication);
    return response;
  };

  /**
   * @description updates publication title
   * @param {string} activityId
   * @param {string} title
   * @param {string} version
   * @returns {object}
   */
  publicationTitleUpdate = async (activityId, title, version) => {
    const message = {
      type: constants.RTM_PUBLICATION_TITLE_UPDATE,
      activityId,
      title,
      version,
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_PUBLICATION_TITLE_UPDATE_OK,
      constants.RTM_PUBLICATION_TITLE_UPDATE_ERROR
    );
    return response;
  };

  fetchPublicationHistory = async (activityId, outputType, market) => {
    const message = {
      type: constants.RTM_PUBLICATION_HISTORY_FETCH,
      activityId,
      ...(outputType && { outputType }), // conditionally add the outputType field only if it exists
      ...(market && { market }), // conditionally add the market field only if it exists
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_PUBLICATION_HISTORY_FETCH_OK,
      constants.RTM_PUBLICATION_HISTORY_FETCH_ERROR
    );
    return response;
  };

  fetchIsOnDemand = async (cohortId, deploymentId) => {
    const message = {
      type: constants.RTM_WORKSPACE_COHORT_ISONDEMAND,
      cohortId,
      deploymentId,
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_WORKSPACE_COHORT_ISONDEMAND_OK,
      constants.RTM_WORKSPACE_COHORT_ISONDEMAND_ERROR,
    );
    return response;
  };

  fetchDeploymentDetails = async (cohortId, deploymentId) => {
    const message = {
      type: constants.RTM_WORKSPACE_DEPLOYMENT_DETAILS,
      cohortId,
      deploymentId,
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_WORKSPACE_DEPLOYMENT_DETAILS_OK,
      constants.RTM_WORKSPACE_DEPLOYMENT_DETAILS_ERROR,
    );
    return response;
  };

  /**
   * Subscribes to a publication job.
   * @param {String} publicationId
   * @param {function} listener
   */
  publicationJobsSubscribe = async (publicationId, listener) => {
    const message = {
      type: constants.RTM_PUBLICATION_JOB_SUBSCRIBE,
      publicationId,
    };

    const subscription = await subscribeToRTM(
      this.connection,
      message,
      listener
    );

    // `await subscribeToRTM(..)` returns null if the subscription cannot be created.
    // In this case return an unsubscribe function that does nothing.
    if (!subscription) {
      const unsubscribeFn = () => { };
      return unsubscribeFn;
    }

    const unsubscribeMessage = {
      type: constants.RTM_PUBLICATION_JOB_UNSUBSCRIBE,
      publicationId,
    };
    const unsubscribeFn = () => subscription.unsubscribe(unsubscribeMessage);
    return unsubscribeFn;
  };

  validateProductId = async ({ productId }) => {
    const message = {
      type: constants.RTM_PRODUCTID_VALIDATION_REQUEST,
      productId,
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_PRODUCTID_VALIDATION_REQUEST_OK,
      constants.RTM_PRODUCTID_VALIDATION_REQUEST_ERROR,
      null,
      true
    );
    return response;
  };

  validatePDZProductId = async ({ productId, activityId, market }) => {
    const message = {
      type: constants.RTM_PDZ_PRODUCTID_VALIDATION_REQUEST,
      productId,
      activityId,
      ...(market && { market }), // conditionally add the market field only if it exists
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_PDZ_PRODUCTID_VALIDATION_REQUEST_OK,
      constants.RTM_PDZ_PRODUCTID_VALIDATION_REQUEST_ERROR,
      null,
      true
    );
    return response;
  };

  fetchPublicationVersionDifference = async (activityId, publicationVerAId, publicationVerBId) => {
    const message = {
      type: constants.RTM_PUBLICATION_VERSION_DIFF,
      activityId,
      publicationVerAId,
      publicationVerBId,
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_PUBLICATION_VERSION_DIFF_OK,
      constants.RTM_PUBLICATION_VERSION_DIFF_ERROR,
      null,
      true
    );
    return response;
  };
}

export default PublicationResolver;
